import { endOfDay, startOfDay } from 'date-fns';
import type { PaginationData } from 'models';
import type { InfiniteData } from 'query';
import { useEffect } from 'react';
import { AxiosErrorHandler, Loading } from 'ui';
import { useUser } from 'features/auth';
import { useNodeTodosQuery, useTodosQuery } from '../../../queries';
import { TodosWidgetEmptyState } from '../todos-widget-empty-state';
import { TodosWidgetList } from '../todos-widget-list';
import { TodosWidgetListFeatured } from '../todos-widget-list-featured';
import { TodosWidgetLoadingState } from '../todos-widget-loading-state';
import { TodosWidgetUpdatedAt } from '../todos-widget-updated-at';
import { useGetNumberOfTodos } from './hooks';

const PAGE_SIZE_FEATURED = 6;
const PAGE_SIZE = 20;

type P = {
  date?: string;
  hideTodosFeedCta?: boolean;
  setNumberOfTodosTotal?: (total?: number) => void;
};

const flattenPagesData = <T,>(data?: InfiniteData<PaginationData<T>>) => data?.pages.flatMap((page) => page.data);

const TodosWidgetContent = ({ date, hideTodosFeedCta, setNumberOfTodosTotal }: P) => {
  const {
    nodeUserData: { _id: userId },
  } = useUser();

  const {
    data: featuredTodos,
    dataUpdatedAt: dataUpdatedAtFeatured,
    error: featuredError,
    fetchNextPage: fetchNextPageFeatured,
    hasNextPage: hasNextPageFeatured,
    isFetching: isFetchingFeatured,
    isLoading: isLoadingFeatured,
    isRefetching: isRefetchingFeatured,
  } = useTodosQuery({ date, featured: true, pageSize: PAGE_SIZE_FEATURED });

  const {
    data: todos,
    dataUpdatedAt,
    error,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isLoading,
    isRefetching,
  } = useTodosQuery({ date, featured: false, pageSize: PAGE_SIZE });

  const {
    data: nodeTodos,
    dataUpdatedAt: dataUpdatedAtNodeTodos,
    error: nodeError,
    fetchNextPage: fetchNextPageNodeTodos,
    hasNextPage: hasNextPageNodeTodos,
    isFetching: isFetchingNodeTodos,
    isLoading: isLoadingNodeTodos,
    isRefetching: isRefetchingNodeTodos,
  } = useNodeTodosQuery({
    filter: {
      endTime: date ? startOfDay(new Date(date)) : undefined,
      starTime: date ? endOfDay(new Date(date)) : undefined,
      types: ['event_rsvp', 'event_enter'],
      userId,
    },
    pagination: { pageSize: PAGE_SIZE },
  });

  const allFeaturedTodos = flattenPagesData(featuredTodos);
  const allTodos = flattenPagesData(todos);
  const allNodeTodos = flattenPagesData(nodeTodos);

  const numberOfTodosTotal = useGetNumberOfTodos({ featuredTodos, nodeTodos, todos });

  useEffect(() => {
    setNumberOfTodosTotal?.(numberOfTodosTotal);
  }, [numberOfTodosTotal, setNumberOfTodosTotal]);

  if (
    [isLoading, isLoadingFeatured, isLoadingNodeTodos].some(Boolean) ||
    !allTodos ||
    !allFeaturedTodos ||
    !allNodeTodos
  ) {
    return (
      <AxiosErrorHandler actions={false} error={featuredError ?? nodeError ?? error}>
        <TodosWidgetLoadingState cols={PAGE_SIZE} />
      </AxiosErrorHandler>
    );
  }

  return (
    <div className="todos-widget-content">
      {[isRefetching, isRefetchingFeatured, isRefetchingNodeTodos].some(Boolean) ? <Loading /> : null}

      {allFeaturedTodos.length === 0 && allTodos.length === 0 && allNodeTodos.length === 0 ? (
        <TodosWidgetEmptyState hideTodosFeedCta={hideTodosFeedCta} />
      ) : (
        <>
          {allFeaturedTodos.length > 0 ? (
            <TodosWidgetListFeatured
              fetchNextPage={fetchNextPageFeatured}
              hasMoreData={hasNextPageFeatured}
              isFetching={isFetchingFeatured}
              items={allFeaturedTodos}
            />
          ) : null}

          {allNodeTodos.length > 0 ? (
            <TodosWidgetList
              fetchNextPage={fetchNextPageNodeTodos}
              hasMoreData={hasNextPageNodeTodos}
              isFetching={isFetchingNodeTodos}
              items={allNodeTodos}
            />
          ) : null}

          {allTodos.length > 0 && !hasNextPageNodeTodos ? (
            <TodosWidgetList
              fetchNextPage={fetchNextPage}
              hasMoreData={hasNextPage}
              isFetching={isFetching}
              items={allTodos}
            />
          ) : null}
        </>
      )}

      <TodosWidgetUpdatedAt updatedAt={[dataUpdatedAtFeatured, dataUpdatedAt, dataUpdatedAtNodeTodos]} />
    </div>
  );
};

export default TodosWidgetContent;
